import { useEffect, useState } from 'react';
import type { Material } from '@obraplan/core/domain/schedule/material';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/input';

/**
 * O vínculo do material de código provisório ao SKU de compras (RN-347).
 *
 * É a decisão que a importação do orçamento não toma por ninguém: o `ORC-…` vira o SKU, a
 * marca de provisório sai, e as linhas do cronograma desse material passam a poder virar
 * requisição. O `ORC-…` fica guardado — é por ele que a reimportação da planilha acha o
 * material de novo.
 */
export function LinkSkuDialog({
  material,
  onOpenChange,
  onSubmit,
  isPending,
  errorMessage,
}: {
  /** O material a vincular; ausente, o diálogo está fechado. */
  material: Material | undefined;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: { materialId: string; sku: string }) => void;
  isPending: boolean;
  errorMessage: string | undefined;
}) {
  const [sku, setSku] = useState('');

  useEffect(() => {
    if (material !== undefined) setSku('');
  }, [material]);

  return (
    <Dialog open={material !== undefined} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Vincular ao SKU de compras</DialogTitle>
          <DialogDescription>
            {material === undefined
              ? ''
              : `${material.code} · ${material.name}. O código provisório do orçamento passa a ser o SKU, e as linhas deste material voltam a poder receber ciência e virar requisição.`}
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (material === undefined) return;
            onSubmit({ materialId: material.id, sku: sku.trim() });
          }}
        >
          <Field
            label="SKU"
            htmlFor="material-sku"
            required
            error={errorMessage}
            hint="O código do item no sistema de compras — confira a especificação antes de vincular."
          >
            <Input
              id="material-sku"
              autoFocus
              value={sku}
              onChange={(event) => setSku(event.target.value)}
            />
          </Field>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={isPending || sku.trim() === ''}>
              {isPending ? 'Vinculando…' : 'Vincular'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
